import React, { FC, forwardRef, RefAttributes } from 'react';
import { get, map } from 'lodash';
import { NavMenuItem } from '../SideNav';
import { sanitizeDOM, setRelAttribute, showComponent } from '@trip-ux/common';
import FooterLogo from './FooterLogo';
import { FooterVariant } from './Footer';
import { useSelector } from 'react-redux';
import { ReduxStore, isReferenceId } from '@trip-ux/redux-store';
import { AUTH_HEADERS } from '@trip-ux/constants';

export interface LegacyFooterProps {
  topNavigationList: NavMenuItem[];
  bottomNavigationList: NavMenuItem[];
  copyrightText: string;
  FooterText: string;
  className?: string;
  variant?: FooterVariant;
}

export const LegacyFooter: FC<LegacyFooterProps & RefAttributes<HTMLDivElement>> = forwardRef(
  (
    { topNavigationList, bottomNavigationList, copyrightText, FooterText, className = '', variant = 'SECONDARY' },
    ref
  ) => {
    const isPrimary = variant === 'PRIMARY';
    const { config, whiteLabel } = useSelector((store: ReduxStore) => ({
      config: store.config,
      whiteLabel: store.userProfile.whiteLabel
    }));

    const { isAdmin } = config;
    const isB2C = config?.options?.salesChannel === AUTH_HEADERS.SALES_CHANNEL_B2C;
    const referenceId = isReferenceId(whiteLabel?.referenceId);
    return (
      <div className="legacy-footer-wrapper">
        <div ref={ref} className={`${className} ${isPrimary ? 'footer' : 'secondary-footer'}`}>
          {(isAdmin || (isB2C && !referenceId)) && (
            <div className="footer-top-text" dangerouslySetInnerHTML={{ __html: sanitizeDOM(FooterText) }}></div>
          )}
          <div className={`${!isPrimary && 'footer-container'}`} role="contentinfo">
          <div className="footer-top">
            <ul className="p-0 m-0">
              {map(topNavigationList, (topNavigation, idx) => (
                <li key={`${topNavigation.navId}-${idx}`} className={`${isPrimary && 'my-2'}`}>
                  <a
                    aria-label={topNavigation.ariaLabel}
                    href={topNavigation.href}
                    id={topNavigation.navId}
                    target={topNavigation.target}
                    rel={setRelAttribute(topNavigation.href)}>
                    <span>{topNavigation.label}</span>
                    {topNavigation.target === '_blank' && <span aria-hidden={true} className="blank-icon" />}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          {showComponent(isPrimary, <hr />)}
          {showComponent(
            isPrimary,
            <div data-testid="primary-footer">
              <ul className="footer-list">
                {map(bottomNavigationList, (bottomNavigation, idx) => {
                  const label = get(bottomNavigation, 'label');
                  const ariaLabel = get(bottomNavigation, 'ariaLabel', label);
                  return (
                    <li key={`${bottomNavigation.navId}-${idx}`} className="footer-logo my-2">
                      <FooterLogo key={`${bottomNavigation.navId}-${idx}`} {...{ ...bottomNavigation, ariaLabel }} />
                    </li>
                  );
                })}
                <li className="footer-text my-2">
                  <div
                    className="footer-buttom-text"
                    dangerouslySetInnerHTML={{ __html: sanitizeDOM(copyrightText) }}></div>
                </li>
              </ul>
            </div>
          )}
          {showComponent(
            !isPrimary,
            <div data-testid="secondary-footer" className="footer-bottom">
              <div
                className="footer-bottom-copyright"
                dangerouslySetInnerHTML={{ __html: sanitizeDOM(copyrightText) }}
              />
              <div className="footer-bottom-content">
                {map(bottomNavigationList, bottomNavigation => {
                  const label = get(bottomNavigation, 'label');
                  const ariaLabel = get(bottomNavigation, 'ariaLabel', label);
                  return <FooterLogo key={bottomNavigation.navId} {...{ ...bottomNavigation, ariaLabel }} />;
                })}
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    );
  }
);

LegacyFooter.displayName = 'LegacyFooter';
export default LegacyFooter;
